import { CREDIT_CARD_NAMES, COUNTRY_CA } from 'constants/AppConstants';
import {
  validateEmail,
  validateText,
  validateCountry,
  validateAddress,
  validateState,
  validateZipCode,
  validatePhoneNumber,
  isValidCybersourceCompanyName,
  getCybersourceCompanyNameInvalidKey,
} from 'utils/validation.util'; // #genai Phase 2 add-payment required-field validation

// #genai Phone / ext / company / Phase 2 names are validated after trim(), so request builders
// must trim too (whitespace-only becomes '', '  Acme  ' is sent as Acme). Non-string / falsy
// inputs collapse to ''.
const trimmedOptional = (value) => (typeof value === 'string' ? value.trim() : value || '');

// #genai First non-blank trimmed candidate. A whitespace-only primary must not hide a real fallback.
const trimmedName = (...candidates) => candidates.map(trimmedOptional).find(Boolean) || '';

export const addressInfoSelector = (address) => ({
  emailAddress: address.emailAddress || '',
  company: address.company || '',
  country: address.country || '',
  phone: address.phone || '',
  phoneExt: address.phoneExt || '',
  secondaryPhone: address.secondaryPhone || '',
  secondaryExt: address.secondaryExt || '',
  address1: address.address1 || '',
  city: address.city || '',
  state: address.state || '',
  zip: address.zip || '',
});

export const addressSelector = ({ address }) => addressInfoSelector(address);

export const paymentInfoSelector = (paymentInfo) => ({
  expirationDateMonth: paymentInfo.cardExpMonth || '',
  expirationDateYear: paymentInfo.cardExpYear || '',
  firstName: paymentInfo.cardFirstName || '',
  lastName: paymentInfo.cardLastName || '',
  cardNumber: paymentInfo.creditCardNumber || '',
  cardType: paymentInfo.creditCardType || '',
  savedCardNumber: paymentInfo.creditCardNumber || '',
});

export const showValidationBanner = (
  paymentInfoErrorsForBanner,
  addressInfoErrorsForBannerDisplay,
) => Object.values(paymentInfoErrorsForBanner).some((val) => val !== '')
  || Object.values(addressInfoErrorsForBannerDisplay).some((val) => val !== '');

export const checkIfMessagesAreUpdated = ((elementRefInfo, addressErrors, paymentErrors) => Object.keys(elementRefInfo).some((key) => {
  if (key in addressErrors) {
    return addressErrors[key] !== elementRefInfo[key].message;
  }
  if (key in paymentErrors) {
    return paymentErrors[key] !== elementRefInfo[key].message;
  }
  return false;
}));

export const updateMessages = (elementRefInfo, addressErrors, paymentErrors) => {
  const updatedElementRefInfo = { ...elementRefInfo };
  Object.keys(elementRefInfo).forEach((key) => {
    if (key in addressErrors) {
      updatedElementRefInfo[key].message = addressErrors[key];
    }
    if (key in paymentErrors) {
      updatedElementRefInfo[key].message = paymentErrors[key];
    }
  });
  return updatedElementRefInfo;
};

export const getCardDataFromPaymentInfo = (paymentInfo) => ({
  cardName: CREDIT_CARD_NAMES[paymentInfo.cardType],
  expMonth: paymentInfo.expirationDateMonth,
  expYear: paymentInfo.expirationDateYear,
  firstName: paymentInfo.firstName,
  lastName: paymentInfo.lastName,
});

// #genai Phase 2 Add-Card body per MYEV_UI_INTEGRATION_GUIDE 3 §2.6. Kept separate from
// getCardDataFromPaymentInfo (Phase 1) so the shared /billing-information main-page save and the
// place-order / manage-payment-options flows stay untouched. The Add-Card page uses the Flex
// microform, which yields a transientToken (JWT) the BFF exchanges server-side — so we keep sending
// transientToken (as Phase 1 does) rather than the post-exchange paymentToken/instrumentIdentifierId/
// maskedPan. The dedicated cardData.companyName field was removed (§2.6): the company / legal-entity
// name lives ONLY on the per-card billing address via cardData.billingCompany (-> dbo.Address.Company).
// The per-card billing address rides INSIDE this body (billingXxx) — the add flow must NOT call the
// account-level address-information API (guardrail §2.6).
export const getPhase2CardRequestBody = (paymentInfo, addressInfo, transientToken, isDefault, consent) => {
  const cardData = {
    cardName: CREDIT_CARD_NAMES[paymentInfo.cardType],
    // #genai Trim so a leading space is not sent as the first Cybersource name character.
    firstName: trimmedOptional(paymentInfo.firstName),
    lastName: trimmedOptional(paymentInfo.lastName),
    expMonth: paymentInfo.expirationDateMonth,
    expYear: paymentInfo.expirationDateYear,
    billingFirstName: trimmedName(addressInfo.firstName, paymentInfo.firstName),
    billingLastName: trimmedName(addressInfo.lastName, paymentInfo.lastName),
    // #genai Trim so a leading space is not sent as the first Cybersource company character.
    billingCompany: trimmedOptional(addressInfo.company),
    billingAddress1: addressInfo.address1 || '',
    billingAddress2: addressInfo.address2 || '',
    billingCity: addressInfo.city || '',
    billingState: addressInfo.state || '',
    billingZip: addressInfo.zip || '',
    billingCountry: addressInfo.country || '',
    // #genai SLN-5064: trim optional phone fields so a whitespace-only value is not persisted as a
    // "phone" (matches the trim() used in getPhase2AddressValidationErrors).
    billingPhone: trimmedOptional(addressInfo.phone),
    // #genai SLN-5064: forward phone ext + secondary phone on the card billing address (mirrors the
    // ACH billTo contract). Backend cardData persist for these is being added; sending them now is
    // additive/backward-compatible (unknown fields are ignored) and unblocks the display once the
    // GET /v1/payment-methods billingAddress echoes them back.
    billingPhoneExt: trimmedOptional(addressInfo.phoneExt),
    billingSecondaryPhone: trimmedOptional(addressInfo.secondaryPhone),
    billingSecondaryExt: trimmedOptional(addressInfo.secondaryExt),
    billingEmailAddress: addressInfo.emailAddress || '',
    isDefault: Boolean(isDefault),
  };
  if (transientToken) cardData.transientToken = transientToken;
  return { cardData, saveCard: Boolean(consent) };
};

export const getAchDataFromPaymentInfo = (paymentInfo) => ({
  firstName: paymentInfo.firstName,
  lastName: paymentInfo.lastName,
  accountType: paymentInfo.accountType,
});

// #genai Phase 2 Add-ACH / Add-Card (Figma 885:10004) open a completely empty billing form (no
// LOAD_BILLING_INFO on these routes), so users can click Save without ever touching the Billing
// Information section. AddressInformation only populates errors on keystrokes, so untouched
// required fields would slip past the "Object.values(addressInfoErrors).every(v==='')" check in
// submitToggleUserBillingInfoSaga. This helper walks every mandatory address field and returns the
// same { field: msg, banner: msg } shape that per-keystroke validation emits, so the submit saga
// can dispatch them via updateAddressInfoErrors before the isValid check. Optional fields
// (phoneExt, secondaryPhone, secondaryExt) are intentionally excluded — their per-keystroke
// errors, if any, stay in Redux untouched.
const PHASE2_REQUIRED_ADDRESS_RULES = [
  {
    id: 'emailAddress',
    validate: (value) => validateEmail(value),
    reqBannerKey: 'address_information__email_required',
    invalidBannerKey: 'address_information__invalid_email',
  },
  {
    id: 'company',
    validate: (value) => isValidCybersourceCompanyName(value),
    // #genai Phase 2 Add-payment copy: "Company name is required" (label reads "Company name").
    reqBannerKey: 'address_information__company_name_required',
    invalidBannerKey: 'address_information__company_name_invalid',
    // #genai Phase 2 company: reason-specific inline copy; banner stays the short invalid key.
    getInvalidInlineKey: (value) => getCybersourceCompanyNameInvalidKey(value),
  },
  {
    id: 'country',
    validate: (value) => validateCountry(value),
    reqBannerKey: 'address_information__country_required',
    invalidBannerKey: 'address_information__invalid_country',
  },
  // #genai Phone is optional on the Phase 2 Add-payment forms (no asterisk in Figma), so it is not
  // included here — an empty phone must not block submit or surface a required error. Format is still
  // validated inline via AddressInformation when a value is entered.
  {
    id: 'address1',
    validate: (value) => validateAddress(value),
    reqBannerKey: 'address_information__address1_required',
    invalidBannerKey: 'address_information__invalid_address1',
  },
  {
    id: 'city',
    validate: (value) => validateText(value),
    reqBannerKey: 'address_information__city_required',
    invalidBannerKey: 'address_information__invalid_city',
  },
  {
    id: 'state',
    // #genai State validity depends on the currently-selected country; wrap so the closure captures it.
    validate: (value, addressInfo) => validateState(addressInfo.country, value),
    reqBannerKey: 'address_information__state_required',
    invalidBannerKey: 'address_information__invalid_state',
  },
  {
    id: 'zip',
    // #genai Zip validity depends on the currently-selected country (US vs CA regex).
    validate: (value, addressInfo) => validateZipCode(addressInfo.country, value),
    reqBannerKey: 'address_information__zip_required',
    invalidBannerKey: 'address_information__invalid_zip',
  },
];

// #genai Phone + Secondary Phone are OPTIONAL on the Phase 2 Add-payment forms (no asterisk in
// Figma), but WHEN a value is entered it must be a valid 10-digit number (SLN-5064 QA: a < 10-digit
// phone previously slipped past submit with no message). Empty stays valid; non-empty reuses the
// shared validatePhoneNumber (XXX-XXX-XXXX or XXXXXXXXXX). Kept separate from the required rules so
// the "empty = required error" behavior is not applied to these optional fields.
const PHASE2_OPTIONAL_PHONE_RULES = [
  {
    id: 'phone',
    validate: (value) => validatePhoneNumber(value),
    invalidBannerKey: 'address_information__invalid_phone',
  },
  {
    id: 'secondaryPhone',
    validate: (value) => validatePhoneNumber(value),
    invalidBannerKey: 'address_information__invalid_secondary_phone',
  },
];

// #genai Values arrive from text inputs (trimmed before the empty check) or selects (already codes).
const trimIfString = (value) => (typeof value === 'string' ? value.trim() : value);

// #genai Canada Phase 2: banner names Province / Postal code instead of State / Zip code.
// Extracted from getPhase2AddressValidationErrors to keep its cognitive complexity within the
// SonarQube S3776 threshold; behavior is unchanged.
const getRequiredBannerKey = (id, reqBannerKey, isCanada) => {
  if (!isCanada) return reqBannerKey;
  if (id === 'state') return 'address_information__province_required';
  if (id === 'zip') return 'address_information__postal_code_required';
  return reqBannerKey;
};

// #genai Email / Zip / Postal: screenshot UX copy for both inline + banner. Other fields keep
// generic inline "This field is incorrect" + specific banner key. Extracted alongside
// getRequiredBannerKey for SonarQube S3776; behavior is unchanged.
const getInvalidMessages = (id, invalidBannerKey, isCanada, fieldInvalidMsg, t) => {
  if (id === 'emailAddress') {
    const emailInvalid = t(invalidBannerKey);
    return { inline: emailInvalid, banner: emailInvalid };
  }
  if (id === 'zip') {
    const zipInvalid = t(isCanada ? 'address_information__invalid_postal_code' : invalidBannerKey);
    return { inline: zipInvalid, banner: zipInvalid };
  }
  // #genai Phase 2 company: specific inline (first-char / charset / PII); short banner.
  if (id === 'company') {
    return { inline: t(invalidBannerKey), banner: t('address_information__company_name_invalid') };
  }
  return { inline: fieldInvalidMsg, banner: t(invalidBannerKey) };
};

export const getPhase2AddressValidationErrors = (addressInfo, t) => {
  const fieldRequiredMsg = t('address_information__field_required');
  const fieldInvalidMsg = t('address_information__invalid_field');
  const isCanada = addressInfo.country === COUNTRY_CA;
  const errors = {};
  const banner = {};
  let isValid = true;

  PHASE2_REQUIRED_ADDRESS_RULES.forEach(({
    id, validate, reqBannerKey, invalidBannerKey, getInvalidInlineKey,
  }) => {
    const rawValue = addressInfo[id];
    const value = trimIfString(rawValue);
    if (!value) {
      errors[id] = fieldRequiredMsg;
      banner[id] = t(getRequiredBannerKey(id, reqBannerKey, isCanada));
      isValid = false;
    } else if (validate(rawValue, addressInfo)) {
      errors[id] = '';
      banner[id] = '';
    } else {
      const resolvedKey = getInvalidInlineKey ? getInvalidInlineKey(rawValue) : invalidBannerKey;
      const invalid = getInvalidMessages(id, resolvedKey, isCanada, fieldInvalidMsg, t);
      errors[id] = invalid.inline;
      banner[id] = invalid.banner;
      isValid = false;
    }
  });

  // #genai Optional phone-format pass: only flag a NON-empty value that fails the 10-digit check.
  // Empty phones clear any prior error and never block submit (fields stay optional).
  // Combined empty/valid arms (identical clear-error blocks) to satisfy SonarQube S1871.
  PHASE2_OPTIONAL_PHONE_RULES.forEach(({ id, validate, invalidBannerKey }) => {
    const rawValue = addressInfo[id];
    const value = trimIfString(rawValue);
    // #genai Empty (after trim) OR valid format → clear field/banner errors; else invalid banner.
    if (!value || validate(rawValue)) {
      errors[id] = '';
      banner[id] = '';
    } else {
      errors[id] = fieldInvalidMsg;
      banner[id] = t(invalidBannerKey);
      isValid = false;
    }
  });

  return { errors, banner, isValid };
};

// #genai Module-level predicate extracted from BillingInformation component to reduce its
// cognitive complexity (SonarQube S3776). Encapsulates the && / || branching so the component
// body contains a single-condition guard instead of a compound expression.
export const shouldShowAffiliateView = (isLoading, isBillingAffiliate, hasAdministerBillingPermission) => (
  !isLoading && (isBillingAffiliate || !hasAdministerBillingPermission)
);

// #genai Module-level predicate extracted from BillingInformation component to reduce its
// cognitive complexity (SonarQube S3776). Combines the null-guard and message-staleness
// check so the component body uses a single-condition if instead of a compound && expression.
export const shouldUpdateElementRefMessages = (elementRefInfo, addressErrors, paymentErrors) => (
  elementRefInfo != null && checkIfMessagesAreUpdated(elementRefInfo, addressErrors, paymentErrors)
);

export const getBankAccountInfoRequestBody = (
  paymentInfo,
  addressInfo,
  transientToken,
  isACHDefault,
  consent, // #genai renamed from ccConsent — holds achConsent for ACH, not credit-card consent
  billPayerId,
) => ({
  billPayerId,
  bankData: {
    transientToken,
    accountType: paymentInfo.accountType,
    // #genai Trim so a leading space is not sent as the first Cybersource name character.
    firstName: trimmedOptional(paymentInfo.firstName),
    lastName: trimmedOptional(paymentInfo.lastName),
  },
  billTo: {
    firstName: trimmedName(addressInfo.firstName, paymentInfo.firstName),
    lastName: trimmedName(addressInfo.lastName, paymentInfo.lastName),
    // #genai Phase 2 guide §2.1/§3: the company / legal-entity name lives ONLY on the billing
    // address (billTo.company -> dbo.Address.Company). The dedicated bankData.companyName field was
    // removed; for Corporate Checking the BFF derives the CyberSource token lastName from this
    // billTo.company server-side (§3.1).
    // #genai Trim so a leading space is not sent as the first Cybersource company character.
    company: trimmedOptional(addressInfo.company),
    address1: addressInfo.address1 || '',
    locality: addressInfo.city || '',
    administrativeArea: addressInfo.state || '',
    postalCode: addressInfo.zip || '',
    country: addressInfo.country || '',
    email: addressInfo.emailAddress || '',
    // #genai SLN-5064: trim optional phone fields so a whitespace-only value is not persisted as a
    // "phone" (matches the trim() used in getPhase2AddressValidationErrors).
    phoneNumber: trimmedOptional(addressInfo.phone),
    // #genai SLN-5064: forward phone ext + secondary phone to ecomm-orch billTo (PR #372); persisted
    // to dbo.Address and returned on GET /v1/payment-methods billingAddress for the summary cards.
    phoneExt: trimmedOptional(addressInfo.phoneExt),
    secondaryPhone: trimmedOptional(addressInfo.secondaryPhone),
    secondaryExt: trimmedOptional(addressInfo.secondaryExt),
  },
  isACHDefault: Boolean(isACHDefault),
  consent: Boolean(consent),
}); // #genai
